/**
 * This component has a subset of features of both PriceOrderContainer and ProductModelFilter
 *
 * * From PriceOrderContainer, getInfoCallback (called on changes on the selected variant or subscription)
 * * From ProductModelFilter, handleGetFilters (called on page load)
 */

import { Module } from 'lib/vendor/terrific/Module';
import { Hook } from 'lib/vendor/terrific/Sandbox';

import SdxAccordion from 'sdx/accordion/Accordion';
import SdxSelect from 'sdx/form/Select';

import { getUniqueIdOfElement } from 'lib/util/helpers';
import { getProfile } from 'lib/personalization/profile';

import { default as makePrice, DEFAULT_PRICE_REPRESENTATION } from './Price';
import { Product } from './Product';
import { Case } from './Subscription';

import {
	Filters,
	ListenerModule,
	RequestProductInfo,
	simpleSubscribe,
	SUBSCRIPTION_TYPES,
} from './Tc.Module.EcommerceProductDataHub';

export default class PriceDetails extends Module implements ListenerModule {

	private retentionAllowed = false;
	private subscriptionsSelect! : SdxSelect;
	private aboDiscountOverrides : any = {};
	private monthText = '';
	private retentionSubscription = '';
	private subscriptionType! : SUBSCRIPTION_TYPES;
	private subscriptionId = '';
	private product! : Product;

	public on(callback : Hook) {
		this.retentionAllowed = this.$ctx.data('retention-allowed');
		this.aboDiscountOverrides = this.$ctx.data('abo-discount-overrides') || {};
		this.monthText = this.$ctx.data('month-text');

		const accordion = new SdxAccordion(this.$ctx.find('.accordion')[0]);
		this.log('SDX Accordion initialized', accordion);

		this.subscriptionsSelect = new SdxSelect(this.$ctx.find('select')[0] as HTMLSelectElement);
		this.log('SDX Select initalized', this.subscriptionsSelect);

		simpleSubscribe(this, this.getInfoCallback.bind(this));

		callback();
	}

	public async after() {
		if (!(await getProfile()).getUser()) {
			this.$ctx.find('.not-logged-in').removeClass('hidden');
		}

		this.fire('getFilters', {
			callback: this.handleGetFilters.bind(this),
		});
	}

	/**
	 * Listen to and save updates from Tc.Module.RetentionResult
	 */
	public onRetentionChange(data : any) {
		if (!data.existingAboName) {
			return;
		}
		this.retentionSubscription = data.existingAboName;
		this.updateSelectedSubscription();
		this.updatePrices();
		this.log('onRetentionChange', data.existingAboName);
	}

	/**
	 * Copied and adapted from ProductModelFilter::handleGetFilters
	 *
	 * Callback handler for the getFilters terrific message.
	 * Triggered on page load.
	 * @param filters - Object with all filters for all product model filter types.
	 */
	private handleGetFilters(filters : Filters) {
		this.log('handleGetFilters');
		const mod = this;
		const $select = jQuery(this.subscriptionsSelect.element);

		for (const filter of filters.subscriptionType) {
			const value = filter.value;
			if (value === SUBSCRIPTION_TYPES.retention) {
				filters.subscriptionId.push(filter);
			}
			if (value === SUBSCRIPTION_TYPES.without) {
				filters.subscriptionId.push(filter);
			}
		}

		// Populate
		let options = '';
		filters.subscriptionId.forEach(item => {
			// 6- The visual design implies a single subscription price is used for 12 and 24 months duration
			let subscriptionPrice = this.product.getSubscriptions().getRecurringCharge(item.value, 12) || makePrice();
			if (item.value in mod.aboDiscountOverrides) {
				// 7-
				subscriptionPrice = makePrice(subscriptionPrice.value() - mod.aboDiscountOverrides[item.value]);
			}
			options += `<option value="${item.value}">${item.text}`;
			if (item.value !== 'retention' && item.value !== 'without') {
				options += ` (${subscriptionPrice.formatted()} / ${this.monthText})`;
			}
			options += '</option>';
		});
		$select.append(options);
		this.subscriptionsSelect.reload();

		// Bind handlers
		$select.on('change', this.handleChanged.bind(this));
	}

	/**
	 * Triggered on each variant change
	 */
	private getInfoCallback(data : any) : void {
		if (data && data.product && data.subscriptionType) {
			this.product = data.product;
			this.subscriptionId = data.subscriptionId;
			this.subscriptionType = data.subscriptionType;
			this.log('getInfoCallback', data.subscriptionType);
			this.updatePrices();
		} else {
			// 16-
			this.$ctx.find('.device-12months').text(DEFAULT_PRICE_REPRESENTATION);
			this.$ctx.find('.device-24months').text(DEFAULT_PRICE_REPRESENTATION);
		}
	}

/*

Price reads on existing PriceOrderContainer::getInfoCallback

USED AND DONE:
	placeholders[mod.PLACEHOLDERS.currentPrice] =
			'5- ' + data.product.getPrice(data.subscriptionId, data.subscriptionDuration).formatted();
	placeholders[mod.PLACEHOLDERS.simplePayMonthlyPrice] = '12- ' + simplePayMonthlyPrice;
	placeholders[mod.PLACEHOLDERS.currentPrice] = '16- ' + emDash;
	placeholders[mod.PLACEHOLDERS.subscriptionPrice] = '6- ' + recurringCharge.formatted();
	placeholders[mod.PLACEHOLDERS.subscriptionPrice] = '7- ' + makePrice(charge).formatted();

USED AND TODO:
	placeholders[mod.PLACEHOLDERS.subscriptionPrice] = '17- ' + emDash;
	placeholders[mod.PLACEHOLDERS.subscriptionPrice] = '18- ' + emDash;
	placeholders[mod.PLACEHOLDERS.subscriptionPrice] = '19- ' + emDash;

UNUSED:
	placeholders[mod.PLACEHOLDERS.minimumPrice] = '1- ' + data.product.getMinimumPrice().formatted();
	placeholders[mod.PLACEHOLDERS.minimumPrice] = '14- ' + emDash;
	placeholders[mod.PLACEHOLDERS.originalPrice] = '2- ' + data.product.getOriginalPrice().formatted();
	placeholders[mod.PLACEHOLDERS.currentPrice] =
			'3- ' + placeholders[mod.PLACEHOLDERS.originalPrice];
			(seems unused because there is not visual design for 'ohne abo' option)
	placeholders[mod.PLACEHOLDERS.subscriptionPrice] = '4- ' + zeroDotEmDash;
	placeholders[mod.PLACEHOLDERS.xtraDiscount] = '8- ' + makePrice(discount).formatted();
	placeholders[mod.PLACEHOLDERS.xtraDiscount] = '9- ';
	placeholders[mod.PLACEHOLDERS.priceInstead] = '10- ' + data.product.getOriginalPrice().formatted();
	placeholders[mod.PLACEHOLDERS.priceInstead] = '11- ';
	placeholders[mod.PLACEHOLDERS.simplePayInitialPrice] =
			'13- ' + data.product.getSimplePayInitialPrice(data.subscriptionId, data.subscriptionDuration);
	placeholders[mod.PLACEHOLDERS.originalPrice] = '15- ' + emDash;

TODO:

*/
	private updatePrices() {
		const mod = this;

		mod.toggleNote('activation-fee', this.subscriptionType === SUBSCRIPTION_TYPES.createNew ||
			(this.subscriptionType === SUBSCRIPTION_TYPES.retention && !mod.retentionAllowed));

		mod.toggleNote('instalments-possible', this.subscriptionType === SUBSCRIPTION_TYPES.without);

		if (this.subscriptionType === SUBSCRIPTION_TYPES.without) {
			mod.selectOhneAbo();
			mod.toggleWithSubscription(false);
		} else {
			// 5-
			for (const duration of [12, 24]) {
				let subscription;
				if (this.subscriptionType === SUBSCRIPTION_TYPES.retention && this.retentionSubscription) {
					subscription = this.product.getSubscriptions().getByName(this.retentionSubscription, duration, Case.Retention);
				} else if (this.subscriptionId) {
					subscription = this.product.getSubscriptions().get(this.subscriptionId, duration, Case.Activation);
				}
				let price;
				if (subscription) {
					price = subscription.getPrice().formatted();
				}
				this.$ctx.find(`.device-${duration}months`).text(price || DEFAULT_PRICE_REPRESENTATION);
			}

			mod.toggleWithSubscription(true);

			// The visual design is displaying different monthly info depending on the duration
			// Therefore we calculate the visibility separately. if (isSimplePayAllowed) {
			const monthlyprice12months = this.product.getSimplePayMonthlyPrice(this.subscriptionId, 12);
			if ((monthlyprice12months !== -1) && (monthlyprice12months.value() > 0)) {
				mod.showMonthlyPrice(monthlyprice12months.formatted(), 12);
			} else {
				mod.hideMonthlyPrice(12);
			}

			const monthlyprice24months = this.product.getSimplePayMonthlyPrice(this.subscriptionId, 24);
			if ((monthlyprice24months !== -1) && (monthlyprice24months.value() > 0)) {
				mod.showMonthlyPrice(monthlyprice24months.formatted(), 24);
			} else {
				mod.hideMonthlyPrice(24);
			}
		}

		this.updateSelectedSubscription(this.subscriptionId || this.subscriptionType);
	}

	private updateSelectedSubscription(id? : string) {
		if (!this.subscriptionsSelect || !this.subscriptionsSelect.element.length) {
			return;
		}

		if (this.subscriptionType === SUBSCRIPTION_TYPES.retention && this.retentionSubscription) {
			// Does the personalized subscription exist in the dropdown?
			let foundId;
			const options = Array.prototype.slice.call(this.subscriptionsSelect.element.childNodes) as HTMLOptionElement[];
			for (const option of options) {
				if (option.innerText && option.innerText.indexOf(this.retentionSubscription) === 0) {
					// Yes
					foundId = option.value;
				}
			}
			if (!foundId) {
				// No, so add it to dropdown
				const personalizedSub = this.product.getSubscriptions().getByName(this.retentionSubscription, 24, Case.Retention);
				if (personalizedSub) {
					foundId = personalizedSub.getId();
					this.subscriptionsSelect.element.add(new Option(this.retentionSubscription, foundId));
				}
			}
			id = foundId;
		}

		if (id) {
			this.subscriptionId = id;
			this.subscriptionsSelect.element.value = id;
			this.subscriptionsSelect.reload();
		}
	}

	private toggleNote(noteClass : string, display : boolean) {
		this.$ctx.find('.note-' + noteClass).toggle(display);
	}

	private selectOhneAbo() {
		this.subscriptionsSelect.element.value = SUBSCRIPTION_TYPES.without;
		this.subscriptionsSelect.reload();
	}

	private toggleWithSubscription(display : boolean) {
		this.$ctx.find('.with-subscription').toggle(display);
	}

	private handleChanged() {
		this.fire('updateStatus', {
			origin: getUniqueIdOfElement(this.$ctx),
			status: this.getStatus(),
		});
	}

	/**
	 * FIXME: This is a 'get' function that also sets stuff; should be renamed and/or refactored.
	 */
	private getStatus() {
		const status : RequestProductInfo = {};
		let value = this.subscriptionsSelect.value as string || null;

		if (value === SUBSCRIPTION_TYPES.retention) {
			status.subscriptionType = SUBSCRIPTION_TYPES.retention;
			value = null;
		} else if (value === SUBSCRIPTION_TYPES.without) {
			status.subscriptionType = SUBSCRIPTION_TYPES.without;
			value = null;
		} else {
			status.subscriptionType = SUBSCRIPTION_TYPES.createNew;
		}

		status.subscriptionId = value;

		return status;
	}

	private showMonthlyPrice(price : string, subscriptionDuration : number) : void {
		const $ctx = this.$ctx;

		$ctx.find(`.monthlyprice-${subscriptionDuration}months`).text(price);
		$ctx.find(`.monthlypricecontainer-${subscriptionDuration}months .not-possible`).hide();
		$ctx.find(`.monthlypricecontainer-${subscriptionDuration}months .possible`).show();
	}

	private hideMonthlyPrice(subscriptionDuration : number) : void {
		const $ctx = this.$ctx;

		$ctx.find(`.monthlypricecontainer-${subscriptionDuration}months .possible`).hide();
		$ctx.find(`.monthlypricecontainer-${subscriptionDuration}months .not-possible`).show();
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/ecommerce/Tc.Module.PriceDetails.ts